import './BdaApplicationEdit.css';

import React, { useEffect, useState } from 'react';
import { Alert, Breadcrumb, Button, Col, Form, Modal, Navbar, Row } from 'react-bootstrap';
import DesignerComponent from '../../components/designer/Designer';
import DesignerPalette from '../../components/designer/DesignerPalette';
import DesignerInspector from '../../components/designer/DesignerInspector';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { cloneDeep } from 'lodash';
import { Applications } from '../../api/Applications';
import { useToasts } from '../../contexts/toasts';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { inspectingItemTypeSelector, setInspectingItem } from '../../slices/inspectorSlice';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { changeMode, resetState, setApplication, setApplicationAccessLevel, setAvailableStorages, setDefaultStorage } from '../../slices/designerSlice';
import { GraphV2, translateGraph, validateApplication } from '../../utils/DesignerUtils';
import { BdaApplication } from '../../models/BdaApplication';
import { Helmet } from 'react-helmet';
import AppLoader from '../../components/common/AppLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { accessLevelOptions, modesOptions } from '../../utils';
import { Select } from 'antd';
import { Datasources } from '../../api/Datasources';
import { RootState } from '../../store';
import { Datasource } from '../../models/Datasource';
import { WorkflowMode } from '../../models/Workflow';
import { AccessLevel, UsedItem } from '../../models/generics';
import { BdaService } from '../../models/BdaService';
import { ReactFlowProvider, useReactFlow } from 'reactflow';

function BdaApplicationEdit() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [applicationName, setApplicationName] = useState("");

  const [initialGraph, setInitialGraph] = useState<GraphV2>();
  const { getNodes, getEdges, setNodes, setEdges } = useReactFlow();

  const [showPalette, setShowPalette] = useState(true);
  const [showInspector, setShowInspector] = useState(true);
  const [mode, setMode] = useState<WorkflowMode>();
  const [selectedMode, setSelectedMode] = useState<WorkflowMode>();
  const [accessLevel, setAccessLevel] = useState<AccessLevel>();
  const [datasources, setDatasources] = useState<Datasource[]>();
  const [datasource, setDatasource] = useState<number>();
  const [showSettingsModal, setShowSettingsModal] = useState(!id);

  const inspectingItemType = useSelector(inspectingItemTypeSelector);
  const usedItems = useSelector((state: RootState) => state.designer.usedItems);
  const availableStorages = useSelector((state: RootState) => state.designer.availableStorages);
  const defaultStorage = useSelector((state: RootState) => state.designer.defaultStorage);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  useEffect(() => {
    if (id) initApplication();
    else if (searchParams.get("shouldImportApp") === "true") importApplication();
    // else dispatch(setGraph([]));

    // designer onDestroy
    return () => {
      dispatch(setInspectingItem(null));
      dispatch(resetState())
      localStorage.removeItem('applicationToImport');
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => setDatasource(undefined), [accessLevel]);

  useEffect(() => {
    if (accessLevel) {
      setDatasources(undefined);
      initDatasources();
    }
    // eslint-disable-next-line
  }, [accessLevel]);

  useEffect(() => {
    if (selectedMode) {
      dispatch(changeMode(selectedMode));
      if (!id) {
        dispatch(setApplicationAccessLevel(accessLevel!));
        dispatch(setDefaultStorage(datasource!));
      }
    }
    // eslint-disable-next-line
  }, [selectedMode]);

  const onInspectorClose = () => setShowInspector(false);
  const handleModeModalClose = () => setShowSettingsModal(false);
  const cancel = () => navigate("/bda-applications");
  const handleModeClick = (mode: WorkflowMode) => () => setMode(mode);

  const cancelInspection = () => {
    // simulating a click event on react-flow pane to trigger onPaneClick handler
    const reactFlowPane = document.getElementsByClassName('react-flow__pane').item(0) as HTMLDivElement;
    reactFlowPane.click();
  }

  const initDatasources = () => {
    const params = new URLSearchParams([['accessLevel', `${accessLevel}`]]);

    Datasources.getDatasources(params)
      .then(
        ({ data }) => {
          if (!availableStorages) dispatch(setAvailableStorages(data));
          setDatasources(data);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasources-error', message, `Datasources Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const importApplication = () => {
    const applicationDefinition = localStorage.getItem('applicationToImport');

    if (!applicationDefinition) {
      setSearchParams(undefined);
      // dispatch(setGraph([]));
      return;
    }

    try {
      const applicationObject = JSON.parse(applicationDefinition);
      const newApplication = new BdaApplication(applicationObject);

      console.log('import application', newApplication);
      dispatch(setApplication({ ...newApplication }));

      const graph = translateGraph(newApplication.workflows[0].procedure);
      setInitialGraph(graph);
      setEdges(graph.edges);
      setNodes(graph.nodes);
    }
    catch (e) {
      console.log('error importing application.\n', e);
      localStorage.removeItem('applicationToImport');
    }

  }

  const initApplication = () => {
    const params = new URLSearchParams();
    params.set("forEditing", `${true}`);

    Applications.getApplication(+id!, params)
      .then(
        ({ data: application }) => {
          setApplicationName(application.name);
          setAccessLevel(application.accessLevel);
          dispatch(setApplication(application));
          const procedure = cloneDeep(application.workflows[0].procedure);
          const fixedGraph = procedure.map(node => {
            if (node.type === "batch" || node.type === "stream") {
              node.data.label = node.data.label || node.data.name || node.data.text;
              node.data.name = node.data.name || node.data.text;
            }
            return node;
          });
          const graph = translateGraph(fixedGraph);
          setInitialGraph(graph);
          setEdges(graph.edges);
          setNodes(graph.nodes);
          dispatch(setDefaultStorage(application.defaultDatasource as number));
          setSelectedMode(application.workflows[0].mode);
        },
        () => {
          showToast('get-application-error', 'Could not get application', 'Application Error', 'danger');
        }
      )
  }

  const fixApplication = (application: BdaApplication) => {
    const services: UsedItem<BdaService>[] = cloneDeep(usedItems.filter(item => item.type === 'batch' || item.type === 'stream'));

    services
      .forEach(service => {
        service.properties!
          .forEach(prop => {
            if (application.workflows[0].mode === "BATCH" && (prop.key.includes('output-dataset') || prop.key.includes('output-model'))) {
              const targetStorage = service.outputs!.find(out => out.id === prop.key)?.targetStorage;
              const assetKey = prop.key.includes('output-dataset') ? "dataset" : "model";
              prop[assetKey] = {
                ...prop[assetKey],
                datasource: availableStorages?.find(storage => storage.id === targetStorage) || undefined,
              }
            }

            if (prop.value === null || prop.value === undefined || prop.value === "") prop.value = prop.defaultValue;

            if (prop.valueType === "JSON") prop.value = JSON.stringify(JSON.parse(prop.value));

            if (prop.key.includes("input-columns")) {
              const datasetKey = prop.key.replace("columns", "dataset");
              const columns = (service.inputs?.find(input => input.id === datasetKey)?.dataset?.columns || []) as any[];
              prop.value = service.cascade ? columns : columns.filter(col => col.selected).map(col => col.name).join(",");
            }
          });

        delete service.inputs;
        delete service.outputs;
        delete service.cascade;
        delete service.type;
      });

    const fixedGraph: any[] = getNodes().map((node) => {
      if (node.type === "dataset") {
        const dataset = usedItems.find(item => item.ref === node.id);
        return {
          ...node,
          data: {
            ...node.data,
            dataset
          }
        };
      }
      else if (node.type === "model") {
        const model = usedItems.find(item => item.ref === node.id);
        return {
          ...node,
          data: {
            ...node.data,
            model,
          }
        };
      }
      else return node;
    });

    application.workflows[0].services = services as BdaService[];
    application.workflows[0].procedure = fixedGraph.concat(...getEdges());

    if (application.workflows[0].mode === 'STREAM') application.workflows[0].targetPlatform = 'K8S';

    return application;
  }

  const handleApplicationSubmit = (application: BdaApplication) => {
    const editApplication = cloneDeep(application);
    const applicationToSubmit = fixApplication(editApplication);
    const validation = validateApplication(applicationToSubmit);

    if (!validation.valid) {
      showToast(
        'application-error',
        (
          <>
            <span>Application services require some missing properties.</span>
            <ul>
              {validation.errors.map((error, index) => (
                <li key={index}>
                  <b>{error.service}</b>
                  <ul>
                    {error.missingProperties.map(prop => (
                      <li key={prop}>{prop}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        ),
        'Missing Information', 'danger');
      return;
    }
    console.log('submitted application:', applicationToSubmit);

    applicationToSubmit.defaultDatasource = applicationToSubmit.defaultDatasource || availableStorages!.find(storage => storage.id === defaultStorage) || availableStorages!.find(storage => storage.default);

    showLoader();

    const promise = id
      ? Applications.updateApplication(+id!, applicationToSubmit)
      : Applications.createApplication(applicationToSubmit)

    promise
      .then(
        ({ data: newApp }) => {
          hideLoader();
          showToast(`application-${id ? "edit" : "create"}-success`, `Application ${id ? "updated" : "created"} successfully`, 'Application Success', 'success');
          navigate(`/bda-applications/${newApp.id}`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast(`application-${id ? "edit" : "create"}-error`, message, `Application Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const togglePalette = () => setShowPalette(!showPalette);
  const toggleInspector = () => setShowInspector(!showInspector);

  const handleModeConfirm = () => {
    setSelectedMode(mode);
    handleModeModalClose();
  }

  return (
    <div className="wrapper">
      <Helmet>
        <title>Application {id ? `${id} Edit` : "Design"}</title>
      </Helmet>

      <Navbar className="p-2 align-items-center border-bottom" bg="light">
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/bda-applications" }}>Applications</Breadcrumb.Item>
          {id && <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/bda-applications/${id}` }}>{applicationName}</Breadcrumb.Item>}
          <Breadcrumb.Item active>{id ? "Edit" : "Create"} Application</Breadcrumb.Item>
        </Breadcrumb>
      </Navbar>

      <Modal backdrop="static" keyboard={false} show={showSettingsModal} onHide={handleModeModalClose}>
        <Modal.Header>
          <Modal.Title>Application Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="py-2 mb-3 text-center">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Select application mode, privacy level and default data source before proceeding:</span>
          </Alert>

          <Row className="mb-3">
            {modesOptions.map((opt, index) => (
              <Col md={6} key={index}>
                <Button className={classNames("mode-button", { selected: mode === opt.value })} variant="light" onClick={handleModeClick(opt.value)}>
                  <FontAwesomeIcon size="3x" icon={opt.icon} />
                  <h6 className="mb-0 mt-2">{opt.label}</h6>
                </Button>
              </Col>
            ))}
          </Row>

          <Form.Group className="d-flex align-items-center mb-3">
            <Col xs={4}>
              <Form.Label>Access Level</Form.Label>
            </Col>
            <Select
              placeholder="Select privacy level"
              value={accessLevel}
              onChange={setAccessLevel}
              className="w-100"
            >
              {accessLevelOptions
                .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      <FontAwesomeIcon icon={option.icon} fixedWidth />
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Form.Group>

          <Form.Group className="d-flex align-items-center">
            <Col xs={4}>
              <Form.Label>Default Data Source</Form.Label>
            </Col>
            <Select
              placeholder="Select default data source"
              value={datasource}
              onChange={setDatasource}
              className="w-100"
              options={datasources?.map(ds => ({ label: ds.name, value: ds.id }))}
              loading={accessLevel && !datasources}
              disabled={!accessLevel}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="light" className="border" onClick={cancel}>Cancel</Button>
          <Button variant="success" disabled={!mode || !accessLevel || !datasource} onClick={handleModeConfirm}>Confirm</Button>
        </Modal.Footer>
      </Modal>

      {(!selectedMode || !getNodes()) && (
        <div className="d-flex justify-content-center py-5">
          <AppLoader size={250} />
        </div>
      )}

      {selectedMode && (
        <Row className="designer-container gx-0">
          <aside className={classNames(['palette-sidebar', { open: showPalette }])}>
            <DesignerPalette />
          </aside>

          <Col>
            <Button className={classNames("trigger palette-trigger", { in: !showPalette })} onClick={togglePalette} variant="light">
              <FontAwesomeIcon icon={solid('bars')} fixedWidth />
            </Button>
            <Button className={classNames("trigger inspector-trigger", { in: !showInspector })} onClick={toggleInspector} variant="light">
              <FontAwesomeIcon icon={solid('eye')} fixedWidth />
            </Button>
            {getNodes() && (
              <DesignerComponent
                showInspector={showInspector}
                showPalette={showPalette}
                onShowInspectorChange={setShowInspector}
                onShowPaletteChange={setShowPalette}
                editMode={searchParams.get("shouldImportApp") === "true" || id !== undefined}
                initialNodes={initialGraph?.nodes}
                initialEdges={initialGraph?.edges}
              // onExportGraph={downloadGraph}
              // onImportGraph={openImportModal}
              />
            )}
          </Col>

          <aside className={classNames(['inspector-sidebar', { open: showInspector }, { 'inspecting-dataset': inspectingItemType?.includes('input-dataset') }])}>
            <DesignerInspector
              onClose={onInspectorClose}
              onApplicationSubmit={handleApplicationSubmit}
              onInspectionCancel={cancelInspection}
            />
          </aside>
        </Row>
      )}
    </div>
  );
}

export default function withProvider() {
  return (
    <ReactFlowProvider>
      <BdaApplicationEdit />
    </ReactFlowProvider>
  )
}
